
import React, { useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Utensils, Home, BookOpen, CalendarDays, ShoppingCart, Heart } from 'lucide-react';
import { 
  NavigationMenu, 
  NavigationMenuList, 
  NavigationMenuItem,
} from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { useUser } from '@/contexts/UserContext';
import { useNavigationState } from '@/hooks/useNavigationState';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import MoreMenu from './navigation/MoreMenu';
import MobileNavigation from './MobileNavigation';

const Navigation: React.FC = React.memo(() => {
  const location = useLocation();
  const { isAuthenticated } = useUser();
  const { canAccessMealPlanner, canAccessShoppingList, canAccessWellness } = useNavigationState();
  
  const isActive = useCallback((path: string) => {
    // Special handling for Recipe Collection and its detail pages
    if (path === '/recipe-collection') {
      return location.pathname === '/recipe-collection' ||
             location.pathname === '/recipes' ||
             location.pathname.startsWith('/recipe/') ||
             location.pathname.startsWith('/composition/');
    }
    // Special handling for homepage/overview
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path;
  }, [location.pathname]);

  const navigationItems = useMemo(() => [
    { 
      title: 'Overview', 
      path: '/', 
      icon: Home, 
      enabled: true 
    },
    { 
      title: 'Recipe Collection', 
      path: '/recipe-collection', 
      icon: BookOpen, 
      enabled: true 
    },
    { 
      title: 'Meal Planner', 
      path: '/meal-planner', 
      icon: CalendarDays, 
      enabled: canAccessMealPlanner,
      disabledTooltip: 'Add recipes first to start meal planning'
    },
    { 
      title: 'Shopping List', 
      path: '/shopping-list', 
      icon: ShoppingCart, 
      enabled: canAccessShoppingList,
      disabledTooltip: 'Plan meals first to generate shopping lists'
    },
    { 
      title: 'Wellness', 
      path: '/wellness', 
      icon: Heart, 
      enabled: canAccessWellness,
      disabledTooltip: 'Available after completing onboarding'
    },
  ], [canAccessMealPlanner, canAccessShoppingList, canAccessWellness]);
  
  return (
    <>
      {/* Mobile Navigation */}
      <MobileNavigation />
      
      {/* Desktop Navigation - now only shows on large screens */}
      <header className="bg-white w-full py-3 px-4 sticky top-0 z-10 hidden lg:block">
        <div className="container mx-auto flex items-center justify-center relative">
          <Link to="/" className="flex items-center gap-2 text-xl font-bold text-primary font-playfair absolute left-0">
            <Utensils className="h-7 w-7 text-recipe" />
            <span className="hidden sm:inline text-recipe lowercase">forkast</span>
          </Link>
          
          {isAuthenticated ? (
            <div className="flex items-center justify-center">
              <TooltipProvider>
                <NavigationMenu>
                  <NavigationMenuList>
                    {navigationItems.map((item) => (
                      <NavigationMenuItem key={item.path}>
                        {item.enabled ? (
                          <Link 
                            to={item.path} 
                            className={cn(
                              "px-4 py-2 text-sm font-medium transition-colors hover:text-recipe rounded-md font-geist",
                              isActive(item.path) ? "text-recipe font-semibold bg-recipe/10" : "text-charcoal"
                            )}
                          >
                            <span className="flex items-center">
                              <item.icon className="h-4 w-4 mr-2" />
                              {item.title}
                            </span>
                          </Link>
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className={cn(
                                "px-4 py-2 text-sm font-medium rounded-md font-geist",
                                "text-muted-foreground opacity-50 cursor-not-allowed"
                              )}>
                                <span className="flex items-center">
                                  <item.icon className="h-4 w-4 mr-2" />
                                  {item.title}
                                </span>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{item.disabledTooltip}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </NavigationMenuItem>
                    ))}
                  </NavigationMenuList>
                </NavigationMenu>
              </TooltipProvider>
            </div>
          ) : null}
          
          <div className="absolute right-0">
            <MoreMenu variant="desktop" />
          </div>
          
        </div>
      </header>
    </>
  );
});

Navigation.displayName = 'Navigation';

export default Navigation;
